import type { ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import NotFound from '../pages/NotFound';
import LoginPage from '../pages/login/page';
import { AuthGuard } from './AuthGuard';
import { PlatformPermissionGuard } from './PlatformPermissionGuard';
import { AdminProfileProvider } from '../contexts/AdminProfileContext';
import DashboardPage from '../pages/dashboard/page';
import AteliersPage from '../pages/ateliers/page';
import SubscriptionsPage from '../pages/subscriptions/page';
import PlansPage from '../pages/plans/page';
import OrderPlansPage from '../pages/order-plans/page';
import PaymentsPage from '../pages/payments/page';
import SettingsPage from '../pages/settings/page';
import SupportPage from '../pages/support/page';
import UsersPage from '../pages/users/page';
import FeatureFlagsPage from '../pages/feature-flags/page';
import AdminRolesPage from '../pages/admin-roles/page';
import NotificationsPage from '../pages/notifications/page';
import ProfilePage from '../pages/profile/page';
import MarketingPage from '../pages/marketing/page';
import LogsPage from '../pages/logs/page';
import ComingSoonPage from '../pages/ComingSoon';
import PaymentGatewaysPage from '../pages/payment-gateways/page';
import DemoTenantsPage from '../pages/demo-tenants/page';
import ExpiredDemoTenantsPage from '../pages/expired-demo-tenants/page';
import TrialPerformancePage from '../pages/demo-tenants/performance/page';
import MessagesPage from '../pages/messages/page';
import RecruitmentJobsPage from '../pages/recruitment/jobs/page';
import RecruitmentJobFormPage from '../pages/recruitment/jobs/form-page';
import RecruitmentApplicationsPage from '../pages/recruitment/applications/page';
import RecruitmentApplicationDetailPage from '../pages/recruitment/applications/detail-page';
import RecruitmentSettingsPage from '../pages/recruitment/settings/page';
import AiSalesOverviewPage from '../features/ai-sales/pages/overview-page';
import AiSalesInboxPage from '../features/ai-sales/pages/inbox-page';
import {
  AiSalesAgentPage,
  AiSalesAnalyticsPage,
  AiSalesFollowUpsPage,
  AiSalesKnowledgePage,
  AiSalesLeadDetailPage,
  AiSalesLeadsPage,
} from '../features/ai-sales/pages/ops-pages';
import CommandCenterPage from '../features/intelligence/pages/CommandCenterPage';
import IntelligenceConversationsPage from '../features/intelligence/pages/ConversationsPage';
import IntelligenceLeadsPage from '../features/intelligence/pages/LeadsPage';
import IntelligenceLeadDetailPage from '../features/intelligence/pages/LeadDetailPage';
import IntelligenceFollowUpsPage from '../features/intelligence/pages/FollowUpsPage';
import IntelligencePlaceholderPage from '../features/intelligence/pages/PlaceholderPage';
import SmartAssistantSettingsPage from '../features/smart-assistant-settings/pages/SmartAssistantSettingsPage';
import ReleasesListPage from '../pages/releases/page';
import ReleaseFormPage from '../pages/releases/form-page';
import ReleasePreviewPage from '../pages/releases/preview-page';
import ProductsListPage from '../pages/releases/products-page';

function guard(permission: string, element: ReactNode) {
  return (
    <AuthGuard>
      <AdminProfileProvider>
        <PlatformPermissionGuard permission={permission}>{element}</PlatformPermissionGuard>
      </AdminProfileProvider>
    </AuthGuard>
  );
}

const routes: RouteObject[] = [
  { path: '/login', element: <Navigate to="/admin/login" replace /> },
  { path: '/admin/login', element: <LoginPage /> },
  { path: '/', element: <AuthGuard><Navigate to="/dashboard" replace /></AuthGuard> },
  { path: '/dashboard', element: guard('view_dashboard', <DashboardPage />) },
  { path: '/ateliers', element: guard('view_ateliers', <AteliersPage />) },
  { path: '/subscriptions', element: guard('view_subscriptions', <SubscriptionsPage />) },
  { path: '/plans', element: guard('view_plans', <PlansPage />) },
  { path: '/order-plans', element: guard('view_order_plans', <OrderPlansPage />) },
  { path: '/demo-tenants', element: guard('view_demo_tenants', <DemoTenantsPage />) },
  { path: '/demo-tenants/:id/performance', element: guard('view_demo_tenants', <TrialPerformancePage />) },
  { path: '/expired-demo-tenants', element: guard('view_demo_tenants', <ExpiredDemoTenantsPage />) },
  { path: '/messages', element: guard('view_dashboard', <MessagesPage />) },
  { path: '/recruitment/jobs/new', element: guard('manage_recruitment_jobs', <RecruitmentJobFormPage />) },
  { path: '/recruitment/jobs/:id', element: guard('manage_recruitment_jobs', <RecruitmentJobFormPage />) },
  { path: '/recruitment/jobs', element: guard('view_recruitment', <RecruitmentJobsPage />) },
  { path: '/recruitment/applications/:id', element: guard('view_recruitment', <RecruitmentApplicationDetailPage />) },
  { path: '/recruitment/applications', element: guard('view_recruitment', <RecruitmentApplicationsPage />) },
  { path: '/recruitment/settings', element: guard('view_recruitment', <RecruitmentSettingsPage />) },
  { path: '/payments', element: guard('view_payments', <PaymentsPage />) },
  { path: '/payment-gateways', element: guard('view_payment_gateways', <PaymentGatewaysPage />) },
  { path: '/users', element: guard('view_users', <UsersPage />) },
  { path: '/admin-roles', element: guard('view_admin_roles', <AdminRolesPage />) },
  { path: '/settings', element: guard('view_settings', <SettingsPage />) },
  { path: '/feature-flags', element: guard('view_flags', <FeatureFlagsPage />) },
  { path: '/support', element: guard('view_tickets', <SupportPage />) },
  { path: '/notifications', element: guard('view_notifications', <NotificationsPage />) },
  { path: '/profile', element: <AuthGuard><AdminProfileProvider><ProfilePage /></AdminProfileProvider></AuthGuard> },
  { path: '/logs', element: guard('view_logs', <LogsPage />) },
  { path: '/integrations', element: guard('view_integrations', <ComingSoonPage />) },
  { path: '/marketing', element: guard('view_marketing', <MarketingPage />) },
  { path: '/ai-sales', element: guard('ai_sales.view', <AiSalesOverviewPage />) },
  { path: '/ai-sales/inbox', element: guard('ai_sales.inbox', <AiSalesInboxPage />) },
  { path: '/ai-sales/inbox/:id', element: guard('ai_sales.inbox', <AiSalesInboxPage />) },
  { path: '/ai-sales/leads', element: guard('ai_sales.leads', <AiSalesLeadsPage />) },
  { path: '/ai-sales/leads/:id', element: guard('ai_sales.leads', <AiSalesLeadDetailPage />) },
  { path: '/ai-sales/follow-ups', element: guard('ai_sales.followups', <AiSalesFollowUpsPage />) },
  { path: '/ai-sales/knowledge', element: guard('ai_sales.knowledge', <AiSalesKnowledgePage />) },
  { path: '/ai-sales/agent', element: guard('ai_sales.agent', <AiSalesAgentPage />) },
  { path: '/ai-sales/analytics', element: guard('ai_sales.analytics', <AiSalesAnalyticsPage />) },
  { path: '/intelligence', element: guard('ai_sales.view', <CommandCenterPage />) },
  { path: '/intelligence/conversations', element: guard('ai_sales.inbox', <IntelligenceConversationsPage />) },
  { path: '/intelligence/conversations/:id', element: guard('ai_sales.inbox', <IntelligenceConversationsPage />) },
  { path: '/intelligence/leads', element: guard('ai_sales.leads', <IntelligenceLeadsPage />) },
  { path: '/intelligence/leads/:id', element: guard('ai_sales.leads', <IntelligenceLeadDetailPage />) },
  { path: '/intelligence/follow-ups', element: guard('ai_sales.followups', <IntelligenceFollowUpsPage />) },
  { path: '/intelligence/customers', element: guard('ai_sales.view', <IntelligencePlaceholderPage />) },
  { path: '/intelligence/demo-accounts', element: guard('ai_sales.view', <IntelligencePlaceholderPage />) },
  { path: '/intelligence/subscriptions', element: guard('ai_sales.view', <IntelligencePlaceholderPage />) },
  { path: '/intelligence/support', element: guard('ai_sales.view', <IntelligencePlaceholderPage />) },
  { path: '/intelligence/knowledge', element: guard('ai_sales.knowledge', <IntelligencePlaceholderPage />) },
  { path: '/intelligence/team', element: guard('ai_sales.agent', <IntelligencePlaceholderPage />) },
  { path: '/intelligence/settings', element: guard('intelligence.settings.view', <SmartAssistantSettingsPage />) },
  { path: '/releases', element: guard('view_releases', <ReleasesListPage />) },
  { path: '/releases/new', element: guard('manage_releases', <ReleaseFormPage />) },
  { path: '/releases/:id/edit', element: guard('manage_releases', <ReleaseFormPage />) },
  { path: '/releases/:id/preview', element: guard('view_releases', <ReleasePreviewPage />) },
  { path: '/releases/products', element: guard('manage_releases', <ProductsListPage />) },
  { path: '*', element: <NotFound /> },
];

export default routes;
